import React, { useMemo, useState } from "react"

import Breadcrumbs from "../../../components/shared/Breadcrumbs"
import { Button } from "../../../components/shared/Buttons"
import { ButtonLink } from "../../../components/shared/Links"
import Typography from "../../../components/shared/Typography"
import GraphQLProvider from "../../../providers/GraphQLProvider"

const statusLabel = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  paused: "Paused",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled"
}

const isEditableCampaign = (campaign) => {
  if (campaign.status === "draft") return true
  if (campaign.status !== "scheduled" || !campaign.scheduledAt) return false
  return new Date(campaign.scheduledAt) > new Date()
}

const NewsletterCampaigns = ({ campaigns }) => {
  const [statusFilter, setStatusFilter] = useState("all")

  const filtered = useMemo(() => {
    if (statusFilter === "all") return campaigns
    return campaigns.filter((c) => c.status === statusFilter)
  }, [campaigns, statusFilter])

  return (
    <div>
      <Breadcrumbs links={[{ href: "/portal/settings", text: "Settings" }]} activePage="Newsletters" />
      <div className="mb-6 flex items-center justify-between gap-4 sm:flex-col sm:items-start">
        <div>
          <Typography as="h1" variant="h3">
            Newsletter Campaigns
          </Typography>
          <Typography variant="body" className="text-gray-dark">
            Create and manage email campaigns to your clients.
          </Typography>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <ButtonLink href="/portal/settings/newsletter/subscribers" variant="secondary">
            Subscribers
          </ButtonLink>
          <ButtonLink href="/portal/settings/newsletter" variant="secondary">
            Mailboxes
          </ButtonLink>
          <ButtonLink href="/portal/settings/newsletter/campaigns/new">New campaign</ButtonLink>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {["all", "draft", "scheduled", "sending", "paused", "completed"].map((status) => (
          <Button
            key={status}
            type={statusFilter === status ? "primary" : "tertiary"}
            onClick={() => setStatusFilter(status)}>
            {status === "all" ? "All" : statusLabel[status] || status}
          </Button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-light">
        <table className="min-w-full divide-y divide-gray-light">
          <thead className="bg-gray-ultralight">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Subject</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Sent</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Opens</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Clicks</th>
              <th className="px-4 py-3 text-left text-sm font-semibold" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-light bg-white">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-dark">
                  No campaigns yet. Create your first newsletter.
                </td>
              </tr>
            ) : (
              filtered.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="px-4 py-3">{campaign.subject || "(No subject)"}</td>
                  <td className="px-4 py-3">{statusLabel[campaign.status] || campaign.status}</td>
                  <td className="px-4 py-3">{campaign.sentCount}</td>
                  <td className="px-4 py-3">{campaign.openCount}</td>
                  <td className="px-4 py-3">{campaign.clickCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3 whitespace-nowrap">
                      <ButtonLink href={`/portal/settings/newsletter/campaigns/${campaign.id}`} variant="link">
                        View
                      </ButtonLink>
                      {isEditableCampaign(campaign) && (
                        <ButtonLink href={`/portal/settings/newsletter/campaigns/${campaign.id}/edit`} variant="link">
                          Edit
                        </ButtonLink>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function NewsletterCampaignsPage(props) {
  return (
    <GraphQLProvider>
      <NewsletterCampaigns {...props} />
    </GraphQLProvider>
  )
}
